import { FaPlus } from "react-icons/fa";
import { MdTune } from "react-icons/md";
import { Badge } from "../components/Badge";
import { Box } from "../components/Box";
import { Button } from "../components/Button";
import { Flex } from "../components/Flex";
import { Input } from "../components/Input";
import Topbar from "../components/Topbar";
import { UserFilter } from "../components/UserFilter";
import { ROUTE } from "../routes";
import { env } from "../utils/env";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUsers } from "../data/useUsers";
import { useMandateCheckList } from "../data/useMandateCheckList";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChecksTable } from "../components/ChecksTable";
import { ChecksFilters } from "../components/ChecksFilters";
import CustomTabs from "../components/CustomTabs";
import { Paragraph } from "../components/Paragraph";
import {
  HiOutlineSquares2X2,
  HiOutlineUser,
  HiOutlineBuildingOffice2,
} from "react-icons/hi2";

import { useClickOutside } from "../hooks/useClickOutside";
import { useAuth } from "../context/AuthContext";
import { useDashboardView } from "../hooks/useDashboardView";
import {
  SCREENING_CHECK_NAMES_BY_TAB,
  peekRestoredScreeningTab,
  screeningView,
} from "../hooks/dashboardViews/screening";
import { ActiveFilterBar, getAppliedFilterCount } from "../components/ActiveFilterBar";
import { PAGE_SIZE_OPTIONS } from "../components/ServerTable";
import { capitalise } from "../utils/string";

const TAB_TYPES = ["", "individual", "entity"] as const;

const Checks = () => {
  const navigate = useNavigate();
  const { userDetails, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const { data: users } = useUsers();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

  // The check catalogue that validates a restored checkType depends on
  // which tab is being restored, which isn't known until useDashboardView
  // itself has resolved. peekRestoredScreeningTab is a one-time read at
  // mount (mirroring the hook's own resolution rule for this one field) so
  // the right catalogue is already loading by the time resolution needs
  // it; the effect below then keeps this in step with the live, resolved
  // tab as the source of truth once it's known, including later tab
  // switches during the session.
  const [effectiveTab, setEffectiveTab] = useState(() =>
    peekRestoredScreeningTab(searchParams, userDetails?.tenant, userDetails?.id),
  );

  const { data: mandateCheckList } = useMandateCheckList(
    false,
    SCREENING_CHECK_NAMES_BY_TAB[effectiveTab],
  );

  const userOptions = users?.data?.results.map(
    ({ first_name, last_name, id, colour }) => ({
      value: id,
      label: `${first_name} ${last_name}`,
      firstName: first_name,
      lastName: last_name,
      colour,
    }),
  );

  const checkTypeOptions = mandateCheckList?.data?.map(({ name }) => ({
    label: capitalise(name.replace(/_/g, " ")),
    value: name,
  }));

  const optionLists = useMemo(
    () => ({
      checkTypes: checkTypeOptions ?? [],
      users: userOptions ?? [],
      pageSizes: PAGE_SIZE_OPTIONS,
    }),
    [checkTypeOptions, userOptions],
  );

  const optionListsReady = Boolean(mandateCheckList?.data && users?.data);

  const {
    view,
    setValue,
    setValues,
    clearValue,
    clearAll,
    appliedKeys,
    isReady: isViewReady,
  } = useDashboardView(screeningView, { optionLists, optionListsReady });

  useEffect(() => {
    if (isViewReady && view.tab !== effectiveTab) {
      setEffectiveTab(view.tab as number);
    }
  }, [isViewReady, view.tab, effectiveTab]);

  const activeTab = view.tab as number;
  const submittedSearchTerm = view.search as string;

  const selectedUser = useMemo(
    () => userOptions?.find((option) => option.value === view.assignedUser) ?? null,
    [userOptions, view.assignedUser],
  );

  const filters = useMemo(
    () => ({
      dateStarted: [view.dateStartedFrom, view.dateStartedTo],
      checkType: view.checkType,
    }),
    [view.dateStartedFrom, view.dateStartedTo, view.checkType],
  );

  const appliedFilterCount = useMemo(
    () => getAppliedFilterCount(appliedKeys, screeningView),
    [appliedKeys],
  );

  // Chips render below the table (see ActiveFilterBar below), so the
  // table's own reserved height needs to shrink to make room for that row -
  // otherwise it overlaps the fixed-position pagination bar under the table.
  const hasFilterChips = appliedKeys.some((key) => screeningView.fields[key]?.chipLabel);

  const filterUserIds = useMemo(
    () => [selectedUser?.value],
    [selectedUser?.value],
  );

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setValue("search", searchTerm);
  };

  const handleResetSearch = () => {
    setSearchTerm("");
    setValue("search", "");
  };

  const handleFilterSubmit = (values) => {
    setValues({
      dateStartedFrom: values.dateStarted?.[0] ?? null,
      dateStartedTo: values.dateStarted?.[1] ?? null,
      checkType: values.checkType,
    });
    setIsFiltersOpen(false);
  };

  const handleSelection = (type) => {
    setIsDropdownOpen(false);
    if (type === "individual") {
      navigate(ROUTE.CHECKS_NEW);
    } else if (type === "company") {
      navigate(ROUTE.CHECKS_NEW_COMPANY);
    }
  };

  useClickOutside(dropdownRef, isDropdownOpen, closeDropdown);

  const tabLabels = [
    (
      <>
        <HiOutlineSquares2X2 size={16} />
        All
      </>
    ),
    (
      <>
        <HiOutlineUser size={16} />
        Individual
      </>
    ),
    (
      <>
        <HiOutlineBuildingOffice2 size={16} />
        Entity
      </>
    ),
  ];

  return (
    <>
      <div>
        <Topbar title="Screening" noBorder>
          <Flex justifyContent="space-between">
            {env.isSearchChecksFeatureOn ? (
              <Flex as="form" ml={4} onSubmit={handleSearchSubmit}>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.currentTarget.value)}
                  type="search"
                  size="small"
                  placeholder="Search"
                  maxWidth={250}
                  mr={1}
                />
                <Button
                  isDisabled={submittedSearchTerm === searchTerm}
                  type="submit"
                >
                  Search
                </Button>

                {submittedSearchTerm && (
                  <Button
                    variant="text"
                    pl="0px"
                    ml={1}
                    onClick={handleResetSearch}
                  >
                    Reset search
                  </Button>
                )}
              </Flex>
            ) : (
              <Box />
            )}
            <Flex>
              <UserFilter
                size="regular"
                options={userOptions}
                value={selectedUser as any}
                onChange={(option: any) => setValue("assignedUser", option?.value ?? null)}
                mr={1}
              />
              {env.isFilterApplicationsFeatureOn && (
                <Button
                  variant="secondary"
                  mr={1}
                  Icon={MdTune}
                  iconPosition="right"
                  onClick={() => setIsFiltersOpen((isOpen) => !isOpen)}
                >
                  Filter
                  {appliedFilterCount > 0 && <Badge ml={1}>{appliedFilterCount}</Badge>}
                </Button>
              )}
              {
                <Flex position="relative" ref={dropdownRef}>
                  <Button
                    Icon={FaPlus}
                    iconSize="16px"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    isDisabled={!hasPermission("screening.run")}
                    title={
                      hasPermission("screening.run")
                        ? undefined
                        : "You don't have permission to run screenings"
                    }
                  >
                    New Screening
                  </Button>

                  {isDropdownOpen && (
                    <Box
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        background: "white",
                        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                        borderRadius: "12px",
                        zIndex: 10,
                        width: "100%",
                      }}
                    >
                      <Paragraph
                        my={0}
                        style={{ padding: "10px", cursor: "pointer" }}
                        onClick={() => handleSelection("individual")}
                      >
                        Individual
                      </Paragraph>
                      <Paragraph
                        my={0}
                        style={{ padding: "10px", cursor: "pointer" }}
                        onClick={() => handleSelection("company")}
                      >
                        Company
                      </Paragraph>
                    </Box>
                  )}
                </Flex>
              }
            </Flex>
          </Flex>
        </Topbar>
        <Flex justifyContent="space-between">
          <CustomTabs
            activeTab={activeTab}
            onTabChange={(index) => setValue("tab", index)}
            customMarginLeft="24px"
            variant="segmented"
            tabs={TAB_TYPES.map((type, index) => ({
              label: tabLabels[index],
              content: activeTab === index && (
                <div>
                  <Box px="24px">
                    <ActiveFilterBar
                      appliedKeys={appliedKeys}
                      schema={screeningView}
                      view={view}
                      optionLists={optionLists}
                      onRemove={clearValue}
                      onClearAll={clearAll}
                    />
                  </Box>
                  <ChecksTable
                    searchTerm={submittedSearchTerm}
                    filterUserIds={filterUserIds}
                    filters={filters}
                    hasFilterChips={hasFilterChips}
                    type={type}
                    initialSorting={[{ id: view.sortBy as string, desc: view.sortDesc as boolean }]}
                    initialPageSize={view.pageSize as number}
                    onSortChange={(sorting) =>
                      setValues({
                        sortBy: sorting[0]?.id ?? "created_on",
                        sortDesc: sorting[0]?.desc ?? true,
                      })
                    }
                    onPageSizeChange={(pageSize) => setValue("pageSize", pageSize)}
                  />
                  <ChecksFilters
                    isOpen={isFiltersOpen}
                    onRequestClose={() => setIsFiltersOpen(false)}
                    onSubmit={handleFilterSubmit}
                    filters={filters}
                    type={type}
                  />
                </div>
              ),
            }))}
          />
        </Flex>
      </div>
    </>
  );
};

export default Checks;
